import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import { useNavigate } from 'react-router-dom';
import AccountApiService from '../../api/AccountApiService';
import PageShell from '../PageShell';
import LedgerAdjustmentModal from '../Modal/AdminFunctions/LedgerAdjustmentModal';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import * as Constants from '../../utilities/Constants';

/**
 * Every tenant carrying a balance, worst arrears first.
 *
 * This is the screen that replaces keeping the list in your head. It also carries the adjustment
 * button, because the ledger opened with balances derived from a yes/no flag that recorded no amount:
 * a tenant who was further behind than one month at the point it was introduced needs their figure
 * corrected by hand, and this is where that is noticed.
 */
export default function OutstandingAccounts(props) {
    const { setSnackbarProperties, isMobile } = props;

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adjusting, setAdjusting] = useState(null);

    const accountApiService = new AccountApiService();
    const navigate = useNavigate();

    async function loadAccounts() {
        setLoading(true);

        const response = await accountApiService.getOutstandingAccounts();

        if (response.success) {
            setAccounts(response.data);
        } else {
            setSnackbarProperties(`Unable to load outstanding accounts: ${response.message}`, 'error');
        }

        setLoading(false);
    }

    useEffect(() => {
        if (!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);

            return;
        }

        document.title = 'Executive Storage Suites - Outstanding Accounts';
        loadAccounts();
    }, []);

    const columns = [
        {
            field: 'tenantName',
            headerName: 'Tenant',
            flex: 1.4,
        },
        {
            field: 'unitNames',
            headerName: 'Units',
            flex: 1,
            renderCell: (params) => params.row.unitNames.join(', '),
        },
        {
            field: 'pastDue',
            headerName: 'Overdue',
            flex: 0.8,
            renderCell: (params) => (
                <Typography
                    variant={'body2'}
                    sx={{ color: params.row.pastDue > 0 ? 'error.main' : 'text.secondary' }}
                >
                    {Constants.USDollar.format(params.row.pastDue)}
                </Typography>
            ),
        },
        {
            field: 'currentDue',
            headerName: 'This month',
            flex: 0.8,
            renderCell: (params) => Constants.USDollar.format(params.row.currentDue),
        },
        {
            field: 'balance',
            headerName: 'Total owed',
            flex: 0.8,
            renderCell: (params) => (
                <Typography variant={'body2'} sx={{ fontWeight: 700 }}>
                    {Constants.USDollar.format(params.row.balance)}
                </Typography>
            ),
        },
        {
            field: 'options',
            headerName: 'Options',
            flex: 0.7,
            filterable: false,
            sortable: false,
            renderCell: (params) => (
                <Button size={'small'} onClick={() => setAdjusting(params.row)}>
                    Adjust
                </Button>
            ),
        },
    ];

    const totalOwed = accounts.reduce((running, account) => running + account.balance, 0);

    return (
        <PageShell
            title={'Outstanding Accounts'}
            subtitle={loading
                ? 'Loading...'
                : `${accounts.length} account${accounts.length === 1 ? '' : 's'}, ${Constants.USDollar.format(totalOwed)} owed`}
            isMobile={isMobile}
            disableCard={isMobile}
        >
            {loading
                ? <Backdrop open={loading}><CircularProgress color={'inherit'} /></Backdrop>
                : accounts.length === 0
                    ? <Box sx={{ padding: '16px' }}>
                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            Every account is up to date.
                        </Typography>
                    </Box>
                    : isMobile
                        ? <Stack spacing={1}>
                            {accounts.map((account) =>
                                <Card key={account.appUserId} variant={'outlined'}>
                                    <CardContent>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography variant={'subtitle1'} sx={{ fontWeight: 700 }}>
                                                {account.tenantName}
                                            </Typography>
                                            <Typography
                                                variant={'subtitle1'}
                                                sx={{ fontWeight: 700, color: account.pastDue > 0 ? 'error.main' : 'text.primary' }}
                                            >
                                                {Constants.USDollar.format(account.balance)}
                                            </Typography>
                                        </Stack>
                                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                                            {account.unitNames.join(', ')}
                                        </Typography>
                                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                                            {`${Constants.USDollar.format(account.pastDue)} overdue, `}
                                            {`${Constants.USDollar.format(account.currentDue)} this month`}
                                        </Typography>
                                        <Button
                                            size={'small'}
                                            sx={{ marginTop: '8px' }}
                                            onClick={() => setAdjusting(account)}
                                        >
                                            Adjust balance
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                        : <Box sx={{ height: '70vh' }}>
                            <DataGrid
                                rows={accounts}
                                columns={columns}
                                getRowId={(row) => row.appUserId}
                                getRowHeight={() => 'auto'}
                                disableRowSelectionOnClick
                            />
                        </Box>
            }

            <LedgerAdjustmentModal
                open={Boolean(adjusting)}
                close={() => setAdjusting(null)}
                account={adjusting}
                setSnackbarProperties={setSnackbarProperties}
                onAdjusted={loadAccounts}
            />
        </PageShell>
    );
}

OutstandingAccounts.propTypes = {
    setSnackbarProperties: PropTypes.func,
    isMobile: PropTypes.bool,
};
